import { useState, useCallback } from 'react';
import { Upload, FileVideo, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useVideoUpload, type UploadProgress } from '@/hooks/useVideoUpload';

interface VideoUploaderProps {
  userId: string;
  onUploadComplete: (filePath: string) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatSpeed(bytesPerSecond: number): string {
  if (bytesPerSecond <= 0) return 'Calculating...';
  if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(0)} KB/s`;
  return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
}

function formatEta(progress: UploadProgress): string {
  if (progress.bytesPerSecond <= 0 || progress.percent >= 100) return '';
  const remaining = progress.totalBytes - progress.bytesTransferred;
  const seconds = Math.ceil(remaining / progress.bytesPerSecond);
  if (seconds < 60) return `${seconds}s remaining`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)}m remaining`;
  return `${Math.floor(seconds / 3600)}h ${Math.ceil((seconds % 3600) / 60)}m remaining`;
}

export default function VideoUploader({ userId, onUploadComplete }: VideoUploaderProps) {
  const { upload, progress, uploading, error, reset } = useVideoUpload();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('video/')) {
      return;
    }
    setSelectedFile(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const [localError, setLocalError] = useState<string | null>(null);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setLocalError(null);
    try {
      const filePath = await upload(selectedFile, userId);
      onUploadComplete(filePath);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setLocalError(msg);
    }
  }, [selectedFile, userId, upload, onUploadComplete]);

  const handleClear = useCallback(() => {
    setSelectedFile(null);
    reset();
  }, [reset]);

  return (
    <div className="space-y-4">
      {!selectedFile ? (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
            dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
          }`}
        >
          <label className="cursor-pointer flex flex-col items-center gap-3">
            <Upload className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Drop a video file here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">MP4 format, up to 500MB</p>
            </div>
            <input type="file" accept="video/*" onChange={handleFileInput} className="hidden" />
          </label>
        </div>
      ) : (
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileVideo className="h-8 w-8 text-primary" />
              <div>
                <p className="text-sm font-medium">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">{formatBytes(selectedFile.size)}</p>
              </div>
            </div>
            {!uploading && (
              <Button variant="ghost" size="icon" onClick={handleClear}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {uploading && (
            <div className="space-y-2">
              <Progress value={progress.percent} className="h-3" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {formatBytes(progress.bytesTransferred)} / {formatBytes(progress.totalBytes)}
                </span>
                <span className="font-medium">{progress.percent}%</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatSpeed(progress.bytesPerSecond)}</span>
                <span>{formatEta(progress)}</span>
              </div>
            </div>
          )}

          {(error || localError) && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error || localError}</p>
              <p className="text-xs text-destructive/70 mt-1">
                If uploads fail silently, check that storage rules and CORS have been configured.
              </p>
            </div>
          )}

          {!uploading && progress.percent < 100 && (
            <Button onClick={handleUpload} className="w-full">
              Upload Video
            </Button>
          )}

          {progress.percent === 100 && !uploading && <p className="text-sm text-green-600 font-medium">Upload complete</p>}
        </div>
      )}
    </div>
  );
}
